import { api } from './client.js';

export const mediaApi = {
  // Returns { media, folders, pagination: {page, per_page, has_more} }.
  // - `folder`: empty string = root (no folder), non-empty = scope, omit = all
  // - `q`: server-side substring search across filename/alt/mime
  // - `page` 1-indexed, `perPage` 1..200 (default 60)
  list: (workspaceId, { folder, page, perPage, q } = {}) => {
    const params = {};
    if (folder !== undefined) params.folder = folder;
    if (page !== undefined) params.page = page;
    if (perPage !== undefined) params.per_page = perPage;
    if (q !== undefined && q !== '') params.q = q;
    return api
      .get(`/workspaces/${workspaceId}/media`, { params })
      .then((r) => r.data);
  },

  // Returns the created Media record. `onProgress(percent)` is called as the
  // upload streams.
  upload: (workspaceId, file, opts = {}) => {
    // Backwards-compatible: callers that pass a single function get treated
    // as just an onProgress callback (the v0.5 / v0.6 / v0.7 signature).
    if (typeof opts === 'function') opts = { onProgress: opts };
    const { folder, alt, onProgress } = opts;

    const form = new FormData();
    form.append('file', file);
    if (folder) form.append('folder', folder);
    if (alt) form.append('alt', alt);

    return api
      .post(`/workspaces/${workspaceId}/media`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (!onProgress || !e.total) return;
          onProgress(Math.round((e.loaded / e.total) * 100));
        },
      })
      .then((r) => r.data.media);
  },

  update: (mediaId, payload) =>
    api.patch(`/media/${mediaId}`, payload).then((r) => r.data.media),

  destroy: (mediaId) => api.delete(`/media/${mediaId}`).then((r) => r.data),

  // Bulk endpoints — workspace-scoped. The server validates that every id
  // belongs to the workspace before applying.
  bulkUpdate: (workspaceId, ids, payload) =>
    api
      .post(`/workspaces/${workspaceId}/media/bulk-update`, { ids, payload })
      .then((r) => r.data),

  bulkDestroy: (workspaceId, ids) =>
    api
      .post(`/workspaces/${workspaceId}/media/bulk-destroy`, { ids })
      .then((r) => r.data),

  // Concurrent upload helper. Streams `files` in parallel up to `concurrency`,
  // calling `onProgress(uploaded, total, currentFilename)` as each completes.
  // Returns an array of created media records (in upload-completion order).
  uploadMany: async (workspaceId, files, opts = {}) => {
    const { folder, concurrency = 3, onProgress, onItem } = opts;
    const results = new Array(files.length);
    let next = 0;
    let done = 0;

    async function worker() {
      while (true) {
        const i = next++;
        if (i >= files.length) return;
        const file = files[i];
        try {
          const media = await mediaApi.upload(workspaceId, file, { folder });
          results[i] = media;
          onItem?.(media, i);
        } catch (err) {
          results[i] = { error: err };
        } finally {
          done++;
          onProgress?.(done, files.length, files[i]?.name);
        }
      }
    }

    const workers = Array.from({ length: Math.min(concurrency, files.length) }, worker);
    await Promise.all(workers);
    return results;
  },
};
